/**
 * TypeScript mirror of the per-file `tags` map the Rust file-ontology Lambda
 * (omicscloud-cdk `tag-file-ontology`) writes onto each `UserDataIndex` row and
 * that the `storage/files` listing returns verbatim.
 *
 * Everything is optional: a row may be untagged (older uploads, or very fresh
 * ones the stream Lambda has not processed yet), a `.box` file carries the box
 * identity fields while a plain file carries `metadata`, and every sub-group is
 * omitted by the Lambda when empty. Consumers must treat absence as "unknown",
 * never as a hard signal.
 *
 * Sources of truth (keep in lockstep): `generic.rs` (`OntologyTags` struct +
 * `to_attr`), `dispatcher.rs` (box shape).
 */

/** One ontology namespace's terms, e.g. `{ type: 'edam', terms: ['edam:format_1929'] }`. */
export interface OntologyTermGroup {
  type: 'edam' | 'omicsbox' | string
  version?: string
  /** CURIEs, most-specific/primary first. */
  terms: string[]
}

export interface FileProvenanceMetadata {
  platform?: string
  platform_model?: string
  sample_name?: string
  read_layout?: string
  read_mate?: number
}

export interface FileReferenceMetadata {
  build?: string
  species?: string
  contig_count?: number
}

export interface FileSequenceMetadata {
  kind?: string
}

export interface FileAlignmentMetadata {
  sort_order?: string
  aligner?: string
  aligner_version?: string
  aligner_command?: string
}

export interface FileVariantMetadata {
  version?: string
  caller?: string
  caller_version?: string
  sample_count?: number
}

export interface FileFastqMetadata {
  quality_encoding?: string
}

export interface FileAnnotationMetadata {
  source?: string
}

export interface FileBedMetadata {
  subtype?: string
}

/** Header-extracted metadata (plain files only), grouped by concern. All optional. */
export interface FileMetadataGroups {
  provenance?: FileProvenanceMetadata
  reference?: FileReferenceMetadata
  sequence?: FileSequenceMetadata
  alignment?: FileAlignmentMetadata
  variant?: FileVariantMetadata
  fastq?: FileFastqMetadata
  annotation?: FileAnnotationMetadata
  bed?: FileBedMetadata
}

/**
 * The `tags` map. Shared top-level triplet (`format`/`category`/`compressed`);
 * plain files add `ontology` + `metadata`, `.box` files add the box identity
 * fields (and `ontology`, but no `metadata`).
 */
export interface FileTags {
  format?: string
  category?: string
  compressed?: boolean
  ontology?: OntologyTermGroup[]
  metadata?: FileMetadataGroups

  // .box only:
  box_id?: string
  box_name?: string
  box_type?: string
  value_class?: string
  domain_class?: string
}

/** Every ontology term CURIE carried by a file's tags, primary-first, de-duplicated. */
export function collectTermUris(tags: FileTags | undefined | null): string[] {
  if (!tags?.ontology) {
    return []
  }
  const seen = new Set<string>()
  const out: string[] = []
  for (const group of tags.ontology) {
    for (const uri of group.terms ?? []) {
      if (uri && !seen.has(uri)) {
        seen.add(uri)
        out.push(uri)
      }
    }
  }
  return out
}
